import { useMemo } from 'react';
import { AvailableExtra, BasketItem, BasketQuote, QuoteProtection } from '~/lib/types/basket-types';
import { OrderBreakdown } from '../order-breakdown/order-breakdown';
import { useTranslation } from 'react-i18next';
import { TranslationNamespaces } from '~/lib/constants';
import { basketSelectedExtras } from '~/lib/basket';
import { useBasketItemPrice } from '~/hooks/use-basket-item-price';
import { ToSurchargeDefaultName } from '~/lib/utils/breakdown';
import { useTaxName } from '@parcel2go/npm-p2g-app';

type QuotePriceProps = {
  quote: BasketQuote;
  basket: BasketItem;
  protection: QuoteProtection;
};

function QuotePrice({ quote, basket, protection }: QuotePriceProps) {
  const { t } = useTranslation(TranslationNamespaces.CHECKOUT);
  const taxName = useTaxName();

  const extras: AvailableExtra[] = useMemo(
    () => basketSelectedExtras(protection, quote, basket),
    [basket, protection, quote]
  );

  const { total, tax, subTotal, basePrice, nonBasePriceItems, localTaxes } = useBasketItemPrice(basket, extras);

  const priceItems = useMemo(() => {
    const output = [
      { name: t('ORDER_BREAKDOWN_BASE_PRICE', { defaultValue: 'Base Price' }), price: basePrice },
      ...nonBasePriceItems.map((x) => ({
        name: t(`ORDER_BREAKDOWN_${x.name.toUpperCase()}_PRICE`, {
          defaultValue: ToSurchargeDefaultName(x.name)
        }),
        price: x.amount
      })),
      ...extras.map((extra) => ({
        name: t(`ORDER_BREAKDOWN_EXTRA_${extra.key.toUpperCase()}`, { defaultValue: extra.name }),
        price: protection.options.some((x) => x.key === extra.key)
          ? protection.options.find((x) => x.key === extra.key)?.price.net || extra.price.net
          : extra.price.net
      })),
      { name: t('ORDER_BREAKDOWN_SUB_TOTAL', { defaultValue: 'Sub Total' }), price: subTotal, highlight: true }
    ];

    if (tax > 0) {
      output.push({ name: t('ORDER_BREAKDOWN_TAX', { defaultValue: '{{taxName}}', taxName: taxName }), price: tax });
    }

    if (localTaxes > 0) {
      output.push({
        name: t('ORDER_BREAKDOWN_LOCAL_TAX', { defaultValue: 'Duty and Local Taxes' }),
        price: localTaxes
      });
    }

    return output;
  }, [extras, protection.options, basePrice, nonBasePriceItems, subTotal, t, tax, localTaxes]);

  const totalItem = useMemo(
    () => ({ name: t('ORDER_BREAKDOWN_TOTAL', { defaultValue: 'Total' }), price: total }),
    [t, total]
  );

  return (
    <OrderBreakdown
      items={priceItems}
      currency={quote.currency}
      total={totalItem}
      title={t('ORDER_BREAKDOWN_TITLE', { defaultValue: 'Order Breakdown' })}
    />
  );
}

export default QuotePrice;
